import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, NonIdealState, Spinner, Alert } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { navigateToOrgRoute } from '../../utils/navigation';
import ConversationList from './ConversationList';
import MessageList from './MessageList';
import TiptapMessageComposer from './TiptapMessageComposer';
import ModelSelector from './ModelSelector';
import DraggableDivider from './DraggableDivider';
import './ChatInterface.css';
import './chat-dark-mode.css';
import InlineEditableText from '../common/InlineEditableText';
import ConversationSidebarDnD from './ConversationSidebarDnD';

const ChatInterface: React.FC = observer(() => {
  const appStore = useAppStore();
  const { chat: chatStore, auth: authStore, user: userStore, preferences } = appStore;
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load chat sidebar collapse state from preferences
    if (preferences.isChatSidebarCollapsed !== chatStore.splitViewCollapsed) {
      chatStore.toggleSplitView();
    }
  }, []);

  useEffect(() => {
    // Initialize chat interface (conversations already loaded by AppInitializer)
    const initializeChat = async () => {
      try {
        // Only load conversations if not already loaded
        if (!chatStore.conversationsLoaded && !chatStore.conversationsLoading) {
          console.log('[ChatInterface] Loading conversations...');
          await chatStore.loadConversations();
        }
        
        // Only load folders if not already loaded
        if (!chatStore.foldersLoaded && !chatStore.foldersLoading) {
          console.log('[ChatInterface] Loading folders...');
          await chatStore.loadFolders();
        }
        
        // If chatId is provided, select and mount that conversation
        if (chatId) {
          await chatStore.selectConversation(chatId);
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize chat:', error);
        setIsInitialized(true);
      }
    };

    initializeChat();

    return () => {
      // Don't disconnect WebSocket here - it's managed by AppInitializer
      // chatStore.disconnectWebSocket();
    };
  }, []); // Empty dependency array - only run once on mount!

  useEffect(() => {
    // Save the currently selected chat to preferences whenever chatId changes
    if (chatId) {
      preferences.setSelectedChatId(chatId);
    }
  }, [chatId, preferences]);

  useEffect(() => {
    // Handle chat ID changes
    if (chatId && chatId !== chatStore.activeConversationId) {
      chatStore.selectConversation(chatId);
    }
  }, [chatId, chatStore]);

  // Navigate to saved chat on initial load if no chat is selected
  useEffect(() => {
    // Only run if:
    // 1. No chat currently selected in URL
    // 2. App is initialized (auth + data loaded)
    // 3. We have conversations available
    // 4. Not currently loading
    if (!chatId && appStore.isInitialized && !chatStore.conversationsLoading && chatStore.conversations.size > 0) {
      let targetChatId: string | null = null;
      
      // First, try to load the saved chat preference
      if (preferences.selectedChatId) {
        const savedChat = chatStore.conversations.get(preferences.selectedChatId);
        if (savedChat) {
          targetChatId = savedChat.id;
          console.log('[ChatInterface] Found saved chat preference:', targetChatId);
        } else {
          console.log('[ChatInterface] Saved chat no longer exists:', preferences.selectedChatId);
        }
      }
      
      // Fallback: Load the most recently updated chat
      if (!targetChatId && chatStore.conversations.size > 0) {
        const sortedChats = Array.from(chatStore.conversations.values()).sort((a, b) => {
          const aTime = new Date(b.updatedAt || b.createdAt).getTime();
          const bTime = new Date(a.updatedAt || a.createdAt).getTime();
          return aTime - bTime;
        });
        targetChatId = sortedChats[0].id;
        console.log('[ChatInterface] No saved preference, loading most recent chat:', targetChatId);
      }
      
      if (targetChatId) {
        console.log('[ChatInterface] Navigating to chat:', targetChatId);
        if (!navigateToOrgRoute(navigate, `/chat/${targetChatId}`)) {
          console.error('Cannot navigate to chat - no organization context');
        }
      }
    }
  }, [chatId, preferences.selectedChatId, chatStore.conversations.size, chatStore.conversationsLoading, appStore.isInitialized, navigate, chatStore.conversations]);

  const handleNewConversation = async () => {
    try {
      const newConversation = await chatStore.createConversation(
        'New Conversation',
        'private'
      );
      // Model is selected separately via selectedModel in the store
      
      if (newConversation && newConversation.id) {
        if (!navigateToOrgRoute(navigate, `/chat/${newConversation.id}`)) {
          console.error('Failed to navigate to new conversation - no organization context');
        }
      } else {
        console.error('Failed to create conversation: No conversation returned');
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const handleConversationSelect = (conversationId: string) => {
    if (!navigateToOrgRoute(navigate, `/chat/${conversationId}`)) {
      console.error('Cannot select conversation - no organization context');
    }
  };

  const handleToggleSplitView = () => {
    chatStore.toggleSplitView();
    preferences.toggleChatSidebarCollapsed();
  };

  const handleDeleteConversation = async () => {
    if (chatStore.activeConversationId) {
      await chatStore.deleteConversation(chatStore.activeConversationId);
      setShowDeleteAlert(false);
      if (!navigateToOrgRoute(navigate, '/chat')) {
        console.error('Cannot navigate after deletion - no organization context');
      }
    }
  };

  if (!isInitialized) {
    return (
      <div className="chat-loading">
          <Spinner size={50} />
        <p>Loading chat...</p>
      </div>
    );
  }

  return (
    <div className="chat-interface" ref={chatContainerRef}>
      {/* Conversation List Panel */}
      <div 
        className={`chat-sidebar ${chatStore.splitViewCollapsed ? 'collapsed' : ''}`}
        style={{ width: chatStore.splitViewCollapsed ? 80 : chatStore.conversationListWidth }}
      >
        <div className="chat-sidebar-header">
          <h3>Conversations</h3>
          <div className="chat-sidebar-actions">
            <Button
              icon={IconNames.PLUS}
              minimal
              onClick={handleNewConversation}
              title="New Conversation"
            />
            <Button
              icon={chatStore.splitViewCollapsed ? IconNames.CHEVRON_RIGHT : IconNames.CHEVRON_LEFT}
              minimal
              onClick={handleToggleSplitView}
              title={chatStore.splitViewCollapsed ? 'Expand' : 'Collapse'}
            />
          </div>
        </div>
        {/* Only render conversation list when not collapsed */}
        {!chatStore.splitViewCollapsed && (
          <ConversationSidebarDnD onSelect={handleConversationSelect} />
        )}
      </div>

      {/* Draggable Divider */}
      {!chatStore.splitViewCollapsed && (
        <DraggableDivider
          onResize={(delta) => {
            const newWidth = Math.max(200, Math.min(600, chatStore.conversationListWidth + delta));
            chatStore.setConversationListWidth(newWidth);
          }}
          orientation="vertical"
        />
      )}

      {/* Main Chat Area */}
      <div className="chat-main">
        {chatStore.activeConversation ? (
          <>
            {/* Chat Header */}
            <div className="chat-header">
              <div className="chat-header-left">
                {chatStore.splitViewCollapsed && (
                  <Button
                    icon={IconNames.MENU}
                    minimal
                    onClick={handleToggleSplitView}
                    className="chat-menu-button"
                  />
                )}
                <InlineEditableText
                  value={chatStore.activeConversation.title}
                  onSave={(newTitle) => chatStore.updateConversation(chatStore.activeConversationId!, { title: newTitle })}
                  className="chat-title"
                  placeholder="Untitled Conversation"
                />
                {chatStore.activeConversation.visibility === 'shared' && (
                  <span className="chat-visibility-badge">Shared</span>
                )}
                {/* Owner Display */}
                <span className="chat-owner-badge">
                  Owner: {chatStore.activeConversation.userId === appStore.currentUser?.id 
                    ? 'You' 
                    : chatStore.activeConversation.creator?.name || 'Unknown'}
                </span>
              </div>
              <div className="chat-header-right">
                <ModelSelector
                  selectedModel={chatStore.selectedModel}
                  availableModels={chatStore.availableModels}
                  onSelectModel={(model) => chatStore.setSelectedModel(model)}
                />
                <Button
                  icon={IconNames.MORE}
                  minimal
                  onClick={() => chatStore.toggleDebugInfo()}
                  title="More Options"
                />
                {/* Fork - only for shared conversations not owned by user */}
                {chatStore.activeConversation.userId !== appStore.currentUser?.id && 
                 chatStore.activeConversation.visibility === 'shared' && (
                  <Button
                    icon={IconNames.FORK}
                    minimal
                    onClick={async () => {
                      const newConv = await chatStore.forkConversation(chatStore.activeConversationId!);
                      if (newConv?.id) {
                        navigate(`/chat/${newConv.id}`);
                      }
                    }}
                    title="Fork Conversation"
                  />
                )}
                {/* Duplicate - only for owned conversations */}
                {chatStore.activeConversation.userId === appStore.currentUser?.id && (
                  <Button
                    icon={IconNames.DUPLICATE}
                    minimal
                    onClick={async () => {
                      const newConv = await chatStore.duplicateConversation(chatStore.activeConversationId!);
                      if (newConv?.id) {
                        navigate(`/chat/${newConv.id}`);
                      }
                    }}
                    title="Duplicate Conversation"
                  />
                )}
                <Button
                  icon={IconNames.EXPORT}
                  minimal
                  onClick={() => chatStore.exportConversation(chatStore.activeConversationId!)}
                  title="Export Conversation"
                />
                {/* Delete - only for owned conversations */}
                {chatStore.activeConversation.userId === appStore.currentUser?.id && (
                  <Button
                    icon={IconNames.TRASH}
                    minimal
                    intent="danger"
                    onClick={() => setShowDeleteAlert(true)}
                    title="Delete Conversation"
                  />
                )}
              </div>
            </div>

            {/* Active Viewers (for shared conversations) */}
            {chatStore.activeConversation.visibility === 'shared' && chatStore.activeViewers.length > 0 && (
              <div className="chat-active-viewers">
                <span>Viewing: </span>
                {chatStore.activeViewers.map(userId => (
                  <span key={userId} className="viewer-badge">
                    {/* Would show user names here - need to resolve user IDs */}
                    User {userId.slice(0, 8)}
                  </span>
                ))}
              </div>
            )}

            {/* Messages Area */}
            <MessageList
              messages={chatStore.messages}
              isLoading={chatStore.isLoading}
              isStreamingResponse={chatStore.isStreamingResponse}
              showDebugInfo={chatStore.showDebugInfo}
              typingUsers={new Set(chatStore.typingUsers)}
            />

            {/* Message Composer */}
            {chatStore.activeConversationId && (
              <TiptapMessageComposer
                conversationId={chatStore.activeConversationId}
                disabled={chatStore.isStreamingResponse}
              />
            )}
          </>
        ) : (
          <NonIdealState
            icon={IconNames.CHAT}
            title="No Conversation Selected"
            description="Select a conversation from the list or create a new one to start chatting."
            action={
              <Button
                icon={IconNames.PLUS}
                intent="primary"
                onClick={handleNewConversation}
              >
                New Conversation
              </Button>
            }
          />
        )}
      </div>
      
      {/* Delete Confirmation Alert */}
      <Alert
        isOpen={showDeleteAlert}
        onCancel={() => setShowDeleteAlert(false)}
        onConfirm={handleDeleteConversation}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        intent="danger"
        icon="trash"
      >
        <p>Are you sure you want to delete this conversation?</p>
        <p><strong>This action cannot be undone.</strong></p>
      </Alert>
    </div>
  );
});

export default ChatInterface;
